import { issueDifficultyValues } from "~/lib/enums"
import { useDifficulties } from "~/hooks/useDifficulties"
import { difficultyLabel } from "~/lib/difficulty"
import { EntityIdentity, UnassignedIdentity } from "~/components/EntityIdentity"
import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core"
import {
	DndContext,
	DragOverlay,
	PointerSensor,
	useDroppable,
	useSensor,
	useSensors
} from "@dnd-kit/core"
import { AnimatePresence, motion } from "motion/react"
import { ChevronRightIcon, PlusIcon } from "lucide-react"
import { Button } from "~/components/ui/button"

import { type ReactNode, useState } from "react"
import { ScrollArea } from "~/components/ui/scroll-area"
import { useTeam } from "~/hooks/useTeam"
import { IssueDifficulty, IssuePriority, IssueStatus } from "~/lib/enums"
import { cn } from "~/lib/utils"
import type { RouterOutputs } from "~/trpc/react"
import { api } from "~/trpc/react"
import { DifficultyIcon } from "../icons/difficulty"
import { PriorityIcon } from "../icons/priority"
import { StatusIcon } from "../icons/status"
import { ChangeDialog } from "./ChangeDialog"
import { IssueCard } from "./IssueCard"

const statusColumns = [
	{
		id: IssueStatus.Queue,
		name: "Queue",
		icon: <StatusIcon status={IssueStatus.Queue} />
	},
	{
		id: IssueStatus.ToDo,
		name: "To Do",
		icon: <StatusIcon status={IssueStatus.ToDo} />
	},
	{
		id: IssueStatus.InProgress,
		name: "In Progress",
		icon: <StatusIcon status={IssueStatus.InProgress} />
	},
	{
		id: IssueStatus.InReview,
		name: "In Review",
		icon: <StatusIcon status={IssueStatus.InReview} />
	},
	{
		id: IssueStatus.Complete,
		name: "Complete",
		icon: <StatusIcon status={IssueStatus.Complete} />
	},
	{
		id: IssueStatus.Blocked,
		name: "Blocked",
		icon: <StatusIcon status={IssueStatus.Blocked} />
	},
	{
		id: IssueStatus.Canceled,
		name: "Canceled",
		icon: <StatusIcon status={IssueStatus.Canceled} />
	}
]

const priorityColumns = [
	{
		id: IssuePriority.Urgent,
		name: "Urgent",
		icon: <PriorityIcon priority={IssuePriority.Urgent} />
	},
	{
		id: IssuePriority.High,
		name: "High",
		icon: <PriorityIcon priority={IssuePriority.High} />
	},
	{
		id: IssuePriority.Medium,
		name: "Medium",
		icon: <PriorityIcon priority={IssuePriority.Medium} />
	},
	{
		id: IssuePriority.Low,
		name: "Low",
		icon: <PriorityIcon priority={IssuePriority.Low} />
	},
	{
		id: IssuePriority.None,
		name: "None",
		icon: <PriorityIcon priority={IssuePriority.None} />
	}
]

function getAssigneeColumns(
	members: NonNullable<RouterOutputs["team"]["get"]>["teamMembers"]
) {
	const columns: { id: string; name: string; icon: ReactNode }[] = []

	for (const member of members) {
		if (!member.orgMember.user) continue

		columns.push({
			id: member.id,
			name: member.orgMember.user.name ?? "Unknown",
			icon: (
				<EntityIdentity
					kind="user"
					variant="avatar"
					size="md"
					name={member.orgMember.user?.name ?? "?"}
					image={member.orgMember.user?.image}
					lastSeenAt={member.orgMember.user?.lastSeenAt}
				/>
			)
		})
	}

	columns.push({
		id: "unassigned",
		name: "Unassigned",
		icon: <UnassignedIdentity size="md" />
	})

	return columns
}

function KanbanColumn({
	column,
	children,
	count,
	onCreateIssue
}: {
	column: { id: string; name: string; icon?: ReactNode }
	children: ReactNode
	count: number
	onCreateIssue: (value: string) => void
}) {
	const [open, setOpen] = useState(true)
	const { setNodeRef, isOver } = useDroppable({
		id: column.id
	})

	return (
		<motion.div
			layout
			initial={{ opacity: 0, x: 50 }}
			animate={{ opacity: 1, x: 0 }}
			exit={{ opacity: 0, x: -50, transition: { duration: 0.2 } }}
			transition={{ duration: 0.2 }}
			className={cn(
				"flex flex-col w-full min-w-70 sm:min-w-[320px] sm:w-80 md:w-[320px] shrink-0 surface-recessed bg-background-tertiary rounded-lg",
				isOver && "ring-2 ring-primary"
			)}
			ref={setNodeRef}
		>
			<motion.div
				layout="position"
				className="flex items-center gap-2 p-3 border-b border-stroke"
			>
				<Button
					variant="ghost"
					size="icon-xs"
					aria-label={`${open ? "Collapse" : "Expand"} ${column.name}`}
					aria-expanded={open}
					onClick={() => setOpen((value) => !value)}
				>
					<ChevronRightIcon
						className={cn(
							"size-3.5 transition-transform motion-reduce:transition-none",
							open && "rotate-90"
						)}
					/>
				</Button>
				{column.icon}
				<span className="font-medium text-text-main">{column.name}</span>
				<motion.span
					key={count}
					initial={{ scale: 0.8, opacity: 0 }}
					animate={{ scale: 1, opacity: 1 }}
					className="ml-auto text-sm text-text-small font-medium"
				>
					{count}
				</motion.span>
				<Button
					variant="ghost"
					size="icon-xs"
					aria-label={`Create issue in ${column.name}`}
					onClick={() => onCreateIssue(column.id)}
				>
					<PlusIcon />
				</Button>
			</motion.div>

			<ScrollArea className={cn("flex-1", !open && "hidden")}>
				<motion.div layout className="p-2 space-y-2">
					<AnimatePresence initial={false}>{children}</AnimatePresence>
				</motion.div>
			</ScrollArea>
		</motion.div>
	)
}

export function Kanban({
	teamIssueData,
	groupBy = "status",
	hideEmptyColumns = false,
	onCreateIssue,
	onIssueOpen
}: {
	teamIssueData: RouterOutputs["issues"]["getTeamIssues"]
	groupBy?: "status" | "priority" | "assignee" | "difficulty"
	hideEmptyColumns?: boolean
	onIssueOpen?: (
		issue: RouterOutputs["issues"]["getTeamIssues"]["issues"][number]
	) => void
	onCreateIssue: (value: string) => void
}) {
	const difficulties = useDifficulties()
	const difficultyColumns = [null, ...issueDifficultyValues]
		.filter(
			(level) =>
				level === null ||
				difficulties.some((option) => option.level === level) ||
				teamIssueData.issues.some((issue) => issue.difficulty === level)
		)
		.map((level) => ({
			id: String(level),
			name: difficultyLabel(level, difficulties),
			icon: <DifficultyIcon difficulty={level} />
		}))
	const { members } = useTeam()
	const [activeIssue, setActiveIssue] = useState<
		RouterOutputs["issues"]["getTeamIssues"]["issues"][number] | null
	>(null)
	const [pendingChange, setPendingChange] = useState<{
		issueId: string
		field: "status" | "priority" | "assignee" | "difficulty"
		value: string
	} | null>(null)
	const [changeDialog, setChangeDialog] = useState<{
		isOpen: boolean
		fromValue: string
		toValue: string
		issueId: string
		field: "status" | "priority" | "assignee" | "difficulty"
	} | null>(null)

	const utils = api.useUtils()
	const editIssue = api.issues.edit.useMutation({
		onMutate: async (data) => {
			// Cancel any outgoing refetches
			await utils.issues.getTeamIssues.cancel()

			// Get the current team ID from the data
			const currentTeamId = data.teamId

			if (!currentTeamId) return { previousIssues: undefined }

			// Snapshot the previous value
			const previousIssues = utils.issues.getTeamIssues.getData({
				teamId: currentTeamId
			})

			// Optimistically update to the new value
			utils.issues.getTeamIssues.setData({ teamId: currentTeamId }, (old) => {
				if (!old) return old
				return {
					...old,
					issues: old.issues.map((issue) => {
						if (issue.id === data.id) {
							return {
								...issue,
								status: data.status ?? issue.status,
								priority: data.priority ?? issue.priority,
								difficulty:
									data.difficulty === undefined
										? issue.difficulty
										: data.difficulty,
								assignee:
									data.assigneeId === null
										? null
										: data.assigneeId !== undefined
											? (members.find((m) => m.id === data.assigneeId) ??
												issue.assignee)
											: issue.assignee
							}
						}
						return issue
					})
				}
			})

			// Return a context object with the snapshotted value
			return { previousIssues }
		},
		onError: (_err, newData, context) => {
			if (context?.previousIssues && newData.teamId) {
				utils.issues.getTeamIssues.setData(
					{ teamId: newData.teamId },
					context.previousIssues
				)
			}
		},
		onSettled: (_data, _error, variables) => {
			utils.issues.getTeamIssues.invalidate()
			if (variables.status !== undefined) {
				utils.favorites.list.invalidate()
				utils.user.getTeams.invalidate()
			}
			setPendingChange(null)
		}
	})

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8
			}
		})
	)

	const columns = (() => {
		switch (groupBy) {
			case "status":
				return statusColumns
			case "priority":
				return priorityColumns
			case "assignee":
				return getAssigneeColumns(members)
			case "difficulty":
				return difficultyColumns
			default:
				return statusColumns
		}
	})()

	const issuesByColumn = columns.reduce<
		Record<string, typeof teamIssueData.issues>
	>((acc, column) => {
		acc[column.id] = teamIssueData.issues.filter((issue) => {
			if (pendingChange?.issueId === issue.id) {
				return pendingChange.value === column.id
			}

			switch (groupBy) {
				case "status":
					return issue.status === column.id
				case "priority":
					return issue.priority === column.id
				case "assignee":
					return column.id === "unassigned"
						? !issue.assignee
						: issue.assignee?.id === column.id
				case "difficulty":
					return String(issue.difficulty) === column.id
				default:
					return false
			}
		})
		return acc
	}, {})

	function handleDragStart(event: DragStartEvent) {
		const issue = teamIssueData.issues.find(
			(issue) => issue.id === event.active.id
		)
		if (issue) setActiveIssue(issue)
	}

	function handleDragEnd(event: DragEndEvent) {
		setActiveIssue(null)
		const { active, over } = event

		if (!over) return

		const issue = teamIssueData.issues.find((issue) => issue.id === active.id)
		const newValue = over.id as string

		if (!issue) return

		let currentValue: string
		switch (groupBy) {
			case "status":
				currentValue = issue.status
				break
			case "priority":
				currentValue = issue.priority
				break
			case "assignee":
				currentValue = issue.assignee?.id ?? "unassigned"
				break
			case "difficulty":
				currentValue = String(issue.difficulty)
				break
			default:
				return
		}

		if (
			groupBy === "difficulty" &&
			newValue !== "null" &&
			!difficulties.some((option) => String(option.level) === newValue)
		)
			return

		if (currentValue !== newValue) {
			setPendingChange({
				issueId: issue.id,
				field: groupBy,
				value: newValue
			})
			setChangeDialog({
				isOpen: true,
				fromValue: currentValue,
				toValue: newValue,
				issueId: issue.id,
				field: groupBy
			})
		}
	}

	return (
		<div className="h-full flex flex-col">
			<div className="flex-1 min-h-0">
				<ChangeDialog
					isOpen={changeDialog?.isOpen ?? false}
					onClose={() => {
						setChangeDialog(null)
						setPendingChange(null)
					}}
					fromValue={changeDialog?.fromValue ?? ""}
					toValue={changeDialog?.toValue ?? ""}
					field={changeDialog?.field ?? "status"}
					isLoading={editIssue.isPending}
					onConfirm={() => {
						if (
							!changeDialog?.toValue ||
							!changeDialog.field ||
							!changeDialog.issueId
						)
							return

						const data: {
							teamId: string
							id: string
							status?: IssueStatus
							priority?: IssuePriority
							assigneeId?: string | null
							difficulty?: IssueDifficulty
						} = {
							teamId: teamIssueData.issues[0]?.teamId ?? "",
							id: changeDialog.issueId
						}

						switch (changeDialog.field) {
							case "status":
								data.status = changeDialog.toValue as IssueStatus
								break
							case "priority":
								data.priority = changeDialog.toValue as IssuePriority
								break
							case "difficulty":
								data.difficulty =
									changeDialog.toValue === "null"
										? null
										: (Number(changeDialog.toValue) as IssueDifficulty)
								break
							case "assignee":
								data.assigneeId =
									changeDialog.toValue === "unassigned"
										? null
										: changeDialog.toValue
								break
						}
						editIssue.mutate(data)
						setChangeDialog(null)
					}}
					fromMember={
						changeDialog?.field === "assignee" &&
						changeDialog.fromValue !== "unassigned"
							? teamIssueData.issues.find(
									(i) => i.assignee?.id === changeDialog.fromValue
								)?.assignee
							: undefined
					}
					toMember={
						changeDialog?.field === "assignee" &&
						changeDialog.toValue !== "unassigned"
							? members.find((m) => m.id === changeDialog.toValue)
							: undefined
					}
				/>
				<DndContext
					sensors={sensors}
					onDragStart={handleDragStart}
					onDragEnd={handleDragEnd}
				>
					<div className="h-full overflow-x-auto">
						<div className="flex gap-4 p-4 min-w-max">
							<AnimatePresence mode="popLayout">
								{columns
									.filter((column) => {
										const columnIssues = issuesByColumn[column.id] ?? []
										return !hideEmptyColumns || columnIssues.length > 0
									})
									.map((column) => {
										const columnIssues = issuesByColumn[column.id] ?? []

										return (
											<KanbanColumn
												key={`${groupBy}-${column.id}`}
												column={column}
												count={columnIssues.length}
												onCreateIssue={onCreateIssue}
											>
												{columnIssues.map((issue, issueIndex) => (
													<motion.div
														key={issue.id}
														initial={{ opacity: 0, y: 20 }}
														animate={{ opacity: 1, y: 0 }}
														exit={{ opacity: 0, scale: 0.95 }}
														transition={{
															duration: 0.15,
															delay: issueIndex * 0.05
														}}
													>
														<IssueCard
															issue={issue}
															onOpen={
																onIssueOpen
																	? () => onIssueOpen(issue)
																	: undefined
															}
															isPending={pendingChange?.issueId === issue.id}
														/>
													</motion.div>
												))}
											</KanbanColumn>
										)
									})}
							</AnimatePresence>
						</div>
					</div>
					<DragOverlay>
						{activeIssue && (
							<div className="w-[320px] opacity-80">
								<IssueCard issue={activeIssue} />
							</div>
						)}
					</DragOverlay>
				</DndContext>
			</div>
		</div>
	)
}
